import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const EditUnit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const unit = location.state?.unit;
    const userType = getUserType();
    const allowEdit = canEdit(userType, "units");

    const [form, setForm] = useState({
        id: "", name: "", project: "", project_id: "", status: "",
        unit_type: "", sale_type: "", plot_area: "", corner: "",
        park_facing: "", unit_size: "", odd_size: "", construction_type: ""
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!allowEdit) { navigate("/dashboard/units"); return; }
        if (!unit) { navigate("/dashboard/units"); return; }
        // Dynamically populate form with all fields from the unit object
        const populated = {};
        Object.keys(form).forEach((key) => {
            populated[key] = unit[key] !== undefined && unit[key] !== null ? String(unit[key]) : "";
        });
        // Also capture any extra fields not in default form
        Object.keys(unit).forEach((key) => {
            if (!(key in populated)) {
                populated[key] = unit[key] !== undefined && unit[key] !== null ? String(unit[key]) : "";
            }
        });
        setForm(populated);
        // eslint-disable-next-line
    }, [unit, navigate, allowEdit]);

    if (!allowEdit) return null;

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        setSuccess("");

        const formData = new URLSearchParams();
        Object.keys(form).forEach((key) => {
            if (key !== "id") formData.append(key, form[key]);
        });

        fetch(`${API_BASE}/api/updatedatas`, {
            method: "POST",
            headers: { Authorization: "1", Table: "units", Id: form.id },
            body: formData,
        })
            .then((r) => r.json())
            .then((json) => {
                setSaving(false);
                if (json.status === true) {
                    setSuccess("Unit updated successfully!");
                    setTimeout(() => navigate("/dashboard/units"), 1500);
                } else {
                    setError(json.message || "Failed to update unit.");
                }
            })
            .catch(() => {
                setSaving(false);
                setError("Error updating unit.");
            });
    };

    // Get the display name for the unit
    const unitDisplay = form.unit_name || form.name || form.unit || `Unit #${form.id}`;

    return (
        <>
            <style>{`
        .eu-page { max-width:900px; margin:0 auto; }
        .eu-hdr { display:flex; align-items:center; gap:14px; margin-bottom:24px; }
        .eu-back { padding:8px 16px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; color:#334155; cursor:pointer; font-size:14px; font-weight:500; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:6px; }
        .eu-back:hover { background:#f1f5f9; border-color:#cbd5e1; }
        .eu-title { font-size:22px; font-weight:700; color:#0f172a; }
        .eu-sub { font-size:13px; color:#64748b; }

        .eu-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:28px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
        .eu-sec { font-size:14px; font-weight:600; color:#0f172a; margin-bottom:16px; padding-bottom:10px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:8px; }
        .eu-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; margin-bottom:28px; }
        .eu-field { display:flex; flex-direction:column; gap:6px; }
        .eu-field.full { grid-column:1/-1; }
        .eu-field label { font-size:12px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:.4px; }
        .eu-field input,.eu-field select,.eu-field textarea { padding:11px 14px; border-radius:10px; border:1px solid #e2e8f0; font-size:14px; font-family:'Inter',sans-serif; color:#1e293b; background:#f8fafc; outline:none; transition:all .2s; }
        .eu-field input:focus,.eu-field select:focus,.eu-field textarea:focus { border-color:#818cf8; box-shadow:0 0 0 3px rgba(99,102,241,.1); background:#fff; }
        .eu-field textarea { min-height:80px; resize:vertical; }

        .eu-actions { display:flex; gap:12px; margin-top:8px; padding-top:20px; border-top:1px solid #f1f5f9; }
        .eu-save { padding:12px 28px; background:linear-gradient(135deg,#6366f1,#818cf8); color:#fff; border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:8px; }
        .eu-save:hover:not(:disabled) { background:linear-gradient(135deg,#4f46e5,#6366f1); box-shadow:0 4px 14px rgba(99,102,241,.3); transform:translateY(-1px); }
        .eu-save:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .eu-cancel { padding:12px 24px; background:#f8fafc; color:#64748b; border:1px solid #e2e8f0; border-radius:10px; font-size:14px; font-weight:500; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; }
        .eu-cancel:hover { background:#f1f5f9; color:#334155; }

        .eu-spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:eu-sp .6s linear infinite; }
        @keyframes eu-sp { to{transform:rotate(360deg)} }

        .eu-alert { padding:12px 16px; border-radius:10px; font-size:13px; margin-bottom:20px; font-weight:500; }
        .eu-alert-ok { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .eu-alert-err { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }

        @media(max-width:768px){
          .eu-grid{grid-template-columns:1fr;}
          .eu-card{padding:20px;}
          .eu-actions{flex-direction:column;}
        }
      `}</style>

            <div className="eu-page">
                <div className="eu-hdr">
                    <button className="eu-back" onClick={() => navigate("/dashboard/units")}>← Back</button>
                    <div>
                        <div className="eu-title">Edit Unit</div>
                        <div className="eu-sub">{unitDisplay}</div>
                    </div>
                </div>

                {success && <div className="eu-alert eu-alert-ok">✅ {success}</div>}
                {error && <div className="eu-alert eu-alert-err">⚠️ {error}</div>}

                <form className="eu-card" onSubmit={handleSubmit}>
                    {/* Unit Identity */}
                    <div className="eu-sec">🏢 Unit Information</div>
                    <div className="eu-grid">
                        <div className="eu-field">
                            <label>Unit Name</label>
                            <input name="name" value={form.name || ""} onChange={handleChange} placeholder="e.g. SHC-39" />
                        </div>
                        <div className="eu-field">
                            <label>Project Name</label>
                            <input name="project" value={form.project || ""} onChange={handleChange} placeholder="e.g. SHC" />
                        </div>
                        <div className="eu-field">
                            <label>Status</label>
                            <select name="status" value={form.status || ""} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Available">Available</option>
                                <option value="Mortgage">Mortgage</option>
                                <option value="Booked">Booked</option>
                                <option value="Registry">Registry</option>
                            </select>
                        </div>
                    </div>

                    {/* Details & Specifications */}
                    <div className="eu-sec">📐 Details & Specifications</div>
                    <div className="eu-grid">
                        <div className="eu-field">
                            <label>Unit Type</label>
                            <input name="unit_type" value={form.unit_type || ""} onChange={handleChange} placeholder="e.g. Plot" />
                        </div>
                        <div className="eu-field">
                            <label>Sale Type</label>
                            <input name="sale_type" value={form.sale_type || ""} onChange={handleChange} placeholder="e.g. Plot" />
                        </div>
                        <div className="eu-field">
                            <label>Construction Type</label>
                            <input name="construction_type" value={form.construction_type || ""} onChange={handleChange} placeholder="e.g. Raw" />
                        </div>
                        <div className="eu-field">
                            <label>Plot Area</label>
                            <input name="plot_area" value={form.plot_area || ""} onChange={handleChange} placeholder="e.g. 1500" />
                        </div>
                        <div className="eu-field">
                            <label>Unit Size</label>
                            <input name="unit_size" value={form.unit_size || ""} onChange={handleChange} placeholder="e.g. 30x50" />
                        </div>
                        <div className="eu-field">
                            <label>Odd Size</label>
                            <input name="odd_size" value={form.odd_size || ""} onChange={handleChange} placeholder="e.g. Yes/No or Dimensions" />
                        </div>
                    </div>

                    {/* Layout Perks */}
                    <div className="eu-sec">🌳 Layout Perks</div>
                    <div className="eu-grid" style={{ marginBottom: 20 }}>
                        <div className="eu-field">
                            <label>Corner</label>
                            <input name="corner" value={form.corner || ""} onChange={handleChange} placeholder="e.g. Yes / No" />
                        </div>
                        <div className="eu-field">
                            <label>Park Facing</label>
                            <input name="park_facing" value={form.park_facing || ""} onChange={handleChange} placeholder="e.g. Yes / No" />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="eu-actions">
                        <button type="submit" className="eu-save" disabled={saving}>
                            {saving && <span className="eu-spinner" />}
                            {saving ? "Saving..." : "💾 Save Changes"}
                        </button>
                        <button type="button" className="eu-cancel" onClick={() => navigate("/dashboard/units")}>Cancel</button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditUnit;
